/**
 * Relations panel — symmetric counterpart to the Concept Explorer, but
 * focused on the workspace's relations rather than its concepts.
 *
 * Header has two toggle buttons:
 * - **VAN** (from) → for each concept, list relations that ORIGINATE
 *   from it. Each relation is shown as `<TypeLabel> [<targetName>]`.
 * - **NAAR** (to) → for each concept, list relations whose TARGET is
 *   that concept. Each relation is shown as `<TypeLabel> [<sourceName>]`.
 *
 * Click a relation row to select it on the canvas (the AttributePanel
 * picks up the selection for editing). Click a concept header to
 * select the concept itself.
 *
 * Pin button persists the open state across reloads (localStorage key
 * `ae.relationsPanelPinned`); the VAN/NAAR choice is also persisted
 * (`ae.relationsDirection`).
 */
import type { Concept, Relation } from "@ae/domain";
import { fuzzyMatchAny } from "@ae/domain";
import { useDeferredValue, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { getRegistry, resolveBilingual } from "../../registry";
import { useSession } from "../../state/session";
import { useUi } from "../../state/ui";
import { useWorkspaceData } from "../../state/workspace";
import { PanelCollapseIcon } from "../PanelCollapseIcon";
import { useMultiSelect } from "../useMultiSelect";
import { usePanelWidth } from "../usePanelWidth";

export function RelationsPanel() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const open = useUi((s) => s.relationsPanelOpen);
  const setOpen = useUi((s) => s.setRelationsPanelOpen);
  const pinned = useUi((s) => s.relationsPanelPinned);
  const setPinned = useUi((s) => s.setRelationsPanelPinned);
  const { width: panelWidth, ResizeHandle } = usePanelWidth(
    "ae.panelWidth.relationsPanel",
    360,
    "right",
  );
  const direction = useUi((s) => s.relationsDirection);
  const setDirection = useUi((s) => s.setRelationsDirection);
  const selectedRelationId = useUi((s) => s.selectedRelationId);
  const selectedConceptId = useUi((s) => s.selectedConceptId);
  const selectRelation = useUi((s) => s.selectRelation);
  const selectConcept = useUi((s) => s.selectConcept);
  const workspaceId = useSession((s) => s.workspaceId);
  const branchId = useSession((s) => s.branchId);
  const revision = useWorkspaceData((s) => s.revision);
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});
  const [query, setQuery] = useState("");
  // Non-blocking filter — zelfde idee als ConceptExplorer.
  const deferredQuery = useDeferredValue(query);
  const multi = useMultiSelect();

  const grouped = useMemo(() => {
    void revision;
    const store = useWorkspaceData.getState().store;
    const reg = getRegistry();

    // Build index: conceptId → relations matching the chosen direction.
    const byConcept = new Map<string, Relation[]>();
    for (const r of store.listRelations()) {
      if (r.tombstoneInBranch) continue;
      const anchorId = direction === "from" ? r.sourceConceptId : r.targetConceptId;
      const arr = byConcept.get(anchorId) ?? [];
      arr.push(r);
      byConcept.set(anchorId, arr);
    }

    // Render only concepts that have at least one matching relation,
    // unless the user typed a query — then we still hide concepts with
    // no matches AFTER filtering by name to keep the list relevant.
    const concepts = store
      .listConcepts()
      .filter((c) => !c.tombstoneInBranch)
      .filter((c) => byConcept.has(c.id))
      .filter((c) => {
        if (!deferredQuery.trim()) return true;
        return fuzzyMatchAny(Object.values(c.name), deferredQuery);
      });

    return concepts
      .map((c) => {
        const relations = (byConcept.get(c.id) ?? []).slice().sort((a, b) => {
          const aRel = reg.getRelationType(a.typeId);
          const bRel = reg.getRelationType(b.typeId);
          const aLabel = aRel ? resolveBilingual(aRel.label, lang) : a.typeId;
          const bLabel = bRel ? resolveBilingual(bRel.label, lang) : b.typeId;
          return aLabel.localeCompare(bLabel);
        });
        return { concept: c, relations };
      })
      .sort((a, b) =>
        resolveBilingual(a.concept.name, lang).localeCompare(
          resolveBilingual(b.concept.name, lang),
        ),
      );
  }, [revision, direction, deferredQuery, lang]);

  if (!open) return null;

  const totalRelationCount = grouped.reduce((sum, g) => sum + g.relations.length, 0);

  // Visible-order of relation ids (skipping rows in collapsed concept
  // groups), used for shift-range multi-select.
  const visibleRelationOrder = grouped.flatMap(({ concept, relations }) =>
    collapsed[concept.id] === true ? [] : relations.map((r) => r.id),
  );

  return (
    <aside
      data-scope="relations-panel"
      className="ledger relative flex h-full min-h-0 shrink-0 flex-col border-(--ld-control) border-r bg-(--ld-surface)"
      style={{ width: panelWidth }}
    >
      <header className="flex shrink-0 items-center justify-between border-(--ld-border) border-b px-3 py-2">
        <div className="flex items-baseline gap-2">
          <h2 className="font-semibold text-(--ld-text) text-sm">{t("app.panels.relations")}</h2>
          <span className="text-(--ld-muted) text-xs">{totalRelationCount}</span>
        </div>
        <div className="flex items-center gap-1">
          <button
            type="button"
            className={`rounded px-1 text-base leading-none ${
              pinned
                ? "text-(--ld-accent) hover:text-(--ld-accent)"
                : "text-(--ld-muted) hover:text-(--ld-text)"
            }`}
            onClick={() => setPinned(!pinned)}
            title={
              pinned ? t("diagramsPanel.pin.pinnedTitle") : t("diagramsPanel.pin.unpinnedTitle")
            }
            aria-label={pinned ? t("diagramsPanel.pin.unpinAria") : t("diagramsPanel.pin.pinAria")}
            aria-pressed={pinned}
          >
            {pinned ? "📌" : "📍"}
          </button>
          <button
            type="button"
            className="flex h-7 w-7 items-center justify-center rounded text-(--ld-text) text-base leading-none hover:bg-(--ld-active)"
            onClick={() => setOpen(false)}
            title={t("settings.common.close")}
          >
            <PanelCollapseIcon />
          </button>
        </div>
      </header>

      <div className="flex shrink-0 flex-col gap-2 border-(--ld-border) border-b px-3 py-2">
        <div className="inline-flex w-fit overflow-hidden rounded border border-(--ld-control)">
          <DirectionToggle
            label={t("relationsPanel.direction.from")}
            active={direction === "from"}
            onClick={() => setDirection("from")}
            title={t("relationsPanel.direction.fromTitle")}
          />
          <DirectionToggle
            label={t("relationsPanel.direction.to")}
            active={direction === "to"}
            onClick={() => setDirection("to")}
            title={t("relationsPanel.direction.toTitle")}
          />
        </div>
        <input
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={t("relationsPanel.searchPlaceholder")}
          className="w-full rounded border border-(--ld-control) bg-(--ld-surface) px-2 py-1 text-sm focus:border-(--ld-accent) focus:outline-none"
        />
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto">
        {grouped.length === 0 ? (
          <p className="px-3 py-4 text-(--ld-muted) text-xs">
            {query
              ? t("relationsPanel.empty.noMatch")
              : direction === "from"
                ? t("relationsPanel.empty.noOutgoing")
                : t("relationsPanel.empty.noIncoming")}
          </p>
        ) : (
          <ul className="flex flex-col gap-1 px-2 py-2">
            {grouped.map(({ concept, relations }) => {
              const isConceptSelected = selectedConceptId === concept.id;
              const isCollapsed = collapsed[concept.id] === true;
              const conceptName =
                resolveBilingual(concept.name, lang) || t("relationsPanel.unnamed");
              return (
                <li key={concept.id}>
                  <button
                    type="button"
                    className={`flex w-full items-center justify-between rounded px-2 py-1 text-left text-sm ${
                      isConceptSelected
                        ? "bg-(--ld-active) font-semibold text-(--ld-accent)"
                        : "font-medium text-(--ld-text) hover:bg-(--ld-raised)"
                    }`}
                    onClick={() => {
                      setCollapsed((prev) => ({ ...prev, [concept.id]: !isCollapsed }));
                      selectConcept(concept.id);
                    }}
                    title={t("relationsPanel.conceptRowTitle")}
                  >
                    <span className="truncate">
                      <span className="mr-1 inline-block w-2.5 text-(--ld-muted)">
                        {isCollapsed ? "▸" : "▾"}
                      </span>
                      {conceptName}
                    </span>
                    <span className="shrink-0 text-(--ld-muted) text-xs">{relations.length}</span>
                  </button>
                  {!isCollapsed && (
                    <ul className="ml-5 mt-0.5 flex flex-col gap-0.5 border-(--ld-border) border-l pl-2">
                      {relations.map((r) => {
                        const otherEndpointId =
                          direction === "from" ? r.targetConceptId : r.sourceConceptId;
                        return (
                          <RelationRow
                            key={r.id}
                            relation={r}
                            otherEndpointId={otherEndpointId}
                            lang={lang}
                            selected={selectedRelationId === r.id}
                            onSelect={() => selectRelation(r.id)}
                            workspaceId={workspaceId}
                            branchId={branchId}
                            multi={multi}
                            visibleOrder={visibleRelationOrder}
                          />
                        );
                      })}
                    </ul>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>
      <ResizeHandle />
    </aside>
  );
}

function DirectionToggle({
  label,
  active,
  onClick,
  title,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
  title: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      className={`px-2 py-0.5 font-medium text-xs ${
        active
          ? "bg-(--ld-primary) text-(--ld-primary-ink)"
          : "bg-(--ld-surface) text-(--ld-text) hover:bg-(--ld-raised)"
      }`}
    >
      {label}
    </button>
  );
}

function RelationRow({
  relation,
  otherEndpointId,
  lang,
  selected,
  onSelect,
  workspaceId,
  branchId,
  multi,
  visibleOrder,
}: {
  relation: Relation;
  otherEndpointId: string;
  lang: string;
  selected: boolean;
  onSelect: () => void;
  workspaceId: string | null;
  branchId: string | null;
  multi: ReturnType<typeof useMultiSelect>;
  visibleOrder: string[];
}) {
  const { t } = useTranslation();
  const reg = getRegistry();
  const store = useWorkspaceData.getState().store;
  const openDiagram = useWorkspaceData((s) => s.openDiagram);
  const relType = reg.getRelationType(relation.typeId);
  const relTypeLabel = relType ? resolveBilingual(relType.label, lang) : relation.typeId;
  const userLabel = resolveBilingual(relation.label, lang).trim();
  const otherConcept = store.getConcept(otherEndpointId);
  const orphan = !otherConcept;
  const otherName = otherConcept
    ? resolveBilingual(otherConcept.name, lang) || t("relationsPanel.unnamed")
    : `(?: ${otherEndpointId.slice(0, 8)})`;
  const inMulti = multi.isSelected(relation.id);
  // Alleen kijken: selecteren mag, slepen en wissen niet.
  const readOnly = useSession((s) => s.readOnly);
  // Per-relatie expansion-state voor "Diagram-voorkomens"-sublijst.
  // Analoog aan de chevron-toggle in de Explorer-detail (commit 6d66f12).
  const [expanded, setExpanded] = useState(false);
  // Per-instance occurrences: één rij per viewState-entry, met
  // instance-pinning-indicator wanneer sourceInstanceId / targetInstanceId
  // gezet zijn (Feature D 2026-05-26).
  const occurrences = expanded
    ? store
        .listDiagrams()
        .filter((d) => d.viewState?.[relation.id] !== undefined)
        .map((d) => ({ diagram: d, ve: d.viewState[relation.id]! }))
    : [];
  return (
    <li>
      <div className="flex items-center gap-1">
        <button
          type="button"
          className="w-3 shrink-0 text-(--ld-muted) text-xs leading-none hover:text-(--ld-text)"
          onClick={() => setExpanded((v) => !v)}
          aria-expanded={expanded}
          aria-label={
            expanded
              ? t("relationsPanel.occurrences.collapse")
              : t("relationsPanel.occurrences.expand")
          }
          title={t("relationsPanel.occurrences.toggleTitle")}
        >
          {expanded ? "▾" : "▸"}
        </button>
        <button
          type="button"
          // Drag-source: laat een relatie naar het canvas slepen om hem
          // op het actieve diagram tevoorschijn te halen (mits beide
          // endpoints daar al staan). Verweesde relaties zijn niet
          // sleepbaar — die hebben geen geldig endpoint om aan te haken.
          draggable={!orphan && !readOnly}
          onDragStart={(e) => {
            if (orphan) return;
            e.dataTransfer.setData("application/ae-existing-relation-id", relation.id);
            e.dataTransfer.effectAllowed = "copy";
          }}
          className={`flex flex-1 items-center gap-1 rounded px-1.5 py-0.5 text-left text-xs ${
            selected
              ? "bg-(--ld-active) text-(--ld-accent)"
              : inMulti
                ? "bg-(--ld-active) text-(--ld-accent)"
                : orphan
                  ? "text-(--ld-negative) hover:bg-(--ld-negative-bg)"
                  : "text-(--ld-text) hover:bg-(--ld-raised)"
          }`}
          onClick={(e) => {
            const mode = multi.handleClick(relation.id, e, visibleOrder);
            if (mode === "single") onSelect();
          }}
          onKeyDown={(e) => {
            if (e.key === "Delete" || e.key === "Backspace") {
              if (!workspaceId || !branchId) return;
              if (useSession.getState().readOnly) return;
              e.preventDefault();
              const ids = inMulti && multi.size > 1 ? Array.from(multi.selectedIds) : [relation.id];
              const message =
                ids.length > 1
                  ? t("relationsPanel.confirmDeleteMany", { n: ids.length })
                  : t("relationsPanel.confirmDeleteOne", {
                      type: relTypeLabel,
                      label: userLabel ? ` (${userLabel})` : "",
                      other: otherName,
                    });
              if (!window.confirm(message)) return;
              const ws = useWorkspaceData.getState();
              for (const id of ids) {
                void ws.removeRelation(workspaceId, branchId, id);
              }
              multi.clear();
            }
          }}
          title={orphan ? t("relationsPanel.orphanTitle") : t("relationsPanel.rowTitle")}
        >
          {orphan && <span aria-hidden>⚠️</span>}
          <span className="font-medium">{relTypeLabel}</span>
          {userLabel && <span className="text-(--ld-muted)">"{userLabel}"</span>}
          <span className="ml-auto truncate text-(--ld-muted)">[{otherName}]</span>
        </button>
      </div>
      {expanded && (
        <ul className="ml-5 mt-0.5 flex flex-col gap-0.5 text-xs">
          {occurrences.length === 0 ? (
            <li className="text-(--ld-muted) italic">{t("relationsPanel.occurrences.none")}</li>
          ) : (
            occurrences.map(({ diagram, ve }) => (
              <li key={diagram.id} className="flex items-center justify-between gap-2">
                <button
                  type="button"
                  className="truncate text-left text-(--ld-accent) hover:underline"
                  onClick={() => {
                    if (!workspaceId) return;
                    void openDiagram(workspaceId, diagram.id);
                    // Focus de relatie zelf op canvas na hydrate (delay
                    // 50ms zodat de diagram-switch eerst kan landen).
                    setTimeout(() => {
                      useUi.getState().requestViewportFocus(relation.id);
                    }, 50);
                  }}
                >
                  {resolveBilingual(diagram.name, lang) || diagram.id}
                  {ve.sourceInstanceId || ve.targetInstanceId ? " — instance-pinned" : ""}
                </button>
                <span className="font-mono text-(--ld-muted) text-xs">{diagram.viewKind}</span>
              </li>
            ))
          )}
        </ul>
      )}
    </li>
  );
}

// `Concept` import keeps Vite happy with the type-only re-export when
// the file's barrel exports get tree-shaken; not actually used at
// runtime here but the type system relies on it.
export type { Concept };
